<script setup>
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { useSalesDetailCommission } from '../composables/useSalesDetailCommission';
import { useSalesCommissionSheetDownload } from '../composables/useSalesCommissionSheetDownload';
import { useSalesCommissionMeiDownload } from '../composables/useSalesCommissionMeiDownload';
import SalesDetailFactList from './SalesDetailFactList.vue';
import SalesDetailCommissionAllocations from './SalesDetailCommissionAllocations.vue';
import SalesDetailCommissionInstallments from './SalesDetailCommissionInstallments.vue';
import SalesDetailCommissionMei from './SalesDetailCommissionMei.vue';
import SalesDetailCommissionResponsibilities from './SalesDetailCommissionResponsibilities.vue';
import SalesDetailCommissionSummaryGeneral from './SalesDetailCommissionSummaryGeneral.vue';
import SalesDetailCommissionSheets from './SalesDetailCommissionSheets.vue';

const props = defineProps({
  summary: { type: Boolean, default: false },
  transaction: { type: Object, required: true },
});
const { t } = useI18n();
const transaction = toRef(props, 'transaction');
const {
  allocationInstallments,
  installments,
  items,
  plan,
  profile,
  responsibilities,
} = useSalesDetailCommission({ transaction, t });
const sheetDownload = useSalesCommissionSheetDownload({ t });
const meiDownload = useSalesCommissionMeiDownload({ t });
const hasSummaryCommission = computed(() => Boolean(plan.value.id));
const downloadSheet = ({ installment, sheet }) =>
  sheetDownload.download({
    installmentId: installment.id,
    artifactId: sheet.id,
    filename: sheet.nome_original,
  });
const downloadMei = installment =>
  meiDownload.download({
    installmentId: installment.id,
    filename: installment.mei_receipt?.filename,
  });
</script>

<template>
  <div
    :class="
      summary
        ? 'mx-auto grid w-full max-w-[1160px] gap-[15px] py-8 lg:py-14'
        : 'grid gap-4'
    "
  >
    <SalesDetailCommissionSheets
      v-if="!summary"
      :downloading-id="sheetDownload.downloadingId.value"
      :error="sheetDownload.error.value"
      :installments="allocationInstallments"
      @download="downloadSheet"
    />
    <SalesDetailCommissionMei
      v-if="!summary"
      :downloading-id="meiDownload.downloadingId.value"
      :error="meiDownload.error.value"
      :installments="allocationInstallments"
      @download="downloadMei"
    />
    <template v-if="summary">
      <SalesDetailCommissionSummaryGeneral
        v-if="hasSummaryCommission"
        :items="items"
        :installments="installments"
        :observations="plan.observacoes || profile.observacao"
      />
      <LaisCard v-else :title="t('SALES.DELIVERY.SUMMARY.COMMISSION.EMPTY')" />
    </template>
    <LaisCard
      v-if="!summary"
      :title="t('SALES.DETAIL.COMMISSION.TITLE')"
      prefix-icon="i-lucide-circle-dollar-sign"
    >
      <SalesDetailFactList :items="items" />
    </LaisCard>
    <LaisCard
      v-if="!summary"
      :title="t('SALES.DETAIL.COMMISSION.INSTALLMENTS_TITLE')"
      prefix-icon="i-lucide-list-ordered"
    >
      <SalesDetailCommissionInstallments :installments="installments" />
    </LaisCard>
    <SalesDetailCommissionAllocations
      v-if="!summary || hasSummaryCommission"
      :installments="allocationInstallments"
      :summary="summary"
    />
    <SalesDetailCommissionResponsibilities
      v-if="!summary"
      :responsibilities="responsibilities"
    />
    <LaisCard
      v-if="!summary"
      :title="t('SALES.DETAIL.OBSERVATIONS')"
      prefix-icon="i-lucide-message-square-text"
    >
      <p
        class="whitespace-pre-wrap text-sm leading-relaxed text-lais-text-body"
      >
        {{
          plan.observacoes ||
          profile.observacao ||
          t('SALES.DETAIL.EMPTY_VALUE')
        }}
      </p>
    </LaisCard>
  </div>
</template>
